import { Box, SxProps, Theme, Typography, useTheme } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InfoIcon } from "@/assets";
import { FormControlNumberInput } from "@/components/formControls/formControlNumberInput";
import { FormControlSelect } from "@/components/formControls/formControlSelect/FormControlSelect";
import { FormControlTextField } from "@/components/formControls/formControlTextField/FormControlTextField";
import { FormControlToggleButtonGroup, ToggleOption } from "@/components/formControls/formControlToggleButtonGroup";
import { useGetErrorMessage } from "@/hooks/useGetErrorMessage";
import { useModuleContext } from "@/hooks/useModuleContext";
import { ModernizationClassEnum, ModernizationMethodEnum, StatusEnum } from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

import { StyledFormGridContainer, StyledFormSection, StyledFormSectionTitle } from "../refineRealEstate.styled";
import { defaultModernizationsMatrix } from "./defaultModernizationEquipmentMatrix";

const toggleButtonGroupStyle: SxProps<Theme> = {
  gap: "4px",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
};

interface ModernizationTabProps {
  readonly?: boolean;
}

type ModernizationValueOption = ToggleOption<ModernizationClassEnum> & { description: string };

export const ModernizationTab = ({ readonly }: ModernizationTabProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });
  const theme = useTheme();
  const { isValuationPackage, isValuationReport } = useModuleContext();
  const getErrorMessage = useGetErrorMessage();

  const {
    control,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useFormContext<PropertyInformation>();

  const { modernization, buildingData } = watch();
  const shouldHideCostsMethod = isValuationPackage || isValuationReport;

  const prevModernizationMethod = useRef<ModernizationMethodEnum | undefined>(undefined);

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    trigger("modernization.refurbishmentYear");
  }, [buildingData.constructionYear, trigger]);

  useEffect(() => {
    trigger();

    if (shouldHideCostsMethod && modernization.method === ModernizationMethodEnum.costs) {
      setValue("modernization", {
        ...modernization,
        method: ModernizationMethodEnum.automatic,
        class: undefined,
        cost: undefined,
        costDescription: undefined,
        matrix: undefined,
      });
      return;
    }

    if (prevModernizationMethod.current === modernization.method) {
      return;
    }
    if (prevModernizationMethod.current !== undefined) {
      if (modernization.method === ModernizationMethodEnum.detailed) {
        setValue("modernization", {
          ...modernization,
          matrix: defaultModernizationsMatrix,
          class: undefined,
          cost: undefined,
          costDescription: undefined,
        });
      }
      if (modernization.method === ModernizationMethodEnum.simple) {
        setValue("modernization", {
          ...modernization,
          matrix: undefined,
          class: ModernizationClassEnum.light,
          cost: undefined,
          costDescription: undefined,
        });
      }
      if (modernization.method === ModernizationMethodEnum.automatic)
        setValue("modernization", {
          ...modernization,
          matrix: undefined,
          class: undefined,
          cost: undefined,
          costDescription: undefined,
        });
    }

    prevModernizationMethod.current = modernization.method;
  }, [modernization.method, modernization, setValue, trigger, shouldHideCostsMethod]);

  const modernizationOptions = [
    {
      value: ModernizationMethodEnum.detailed,
      label: t("modernizationsMethod.values.dotGrid"),
      disabled: !!readonly,
    },
    {
      value: ModernizationMethodEnum.simple,
      label: t("modernizationsMethod.values.modernizationClasses"),
      disabled: !!readonly,
    },
    ...(!shouldHideCostsMethod
      ? [
          {
            value: ModernizationMethodEnum.costs,
            label: t("modernizationsMethod.values.modernizationCosts"),
            disabled: !!readonly,
          },
        ]
      : []),
    {
      value: ModernizationMethodEnum.automatic,
      label: t("modernizationsMethod.values.fullyAutomatic"),
      disabled: !!readonly,
    },
  ];

  const modernizationMatrixOptions: ToggleOption<StatusEnum>[] = useMemo(
    () => [
      { value: StatusEnum.none, label: t("none"), disabled: !!readonly },
      { value: StatusEnum.partial, label: t("partial"), disabled: !!readonly },
      { value: StatusEnum.full, label: t("full"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const modernizationClassOptions: ModernizationValueOption[] = useMemo(
    () => [
      {
        value: ModernizationClassEnum.none,
        label: t("modernizationValue.none"),
        description: t("modernizationValue.none.description"),
        disabled: !!readonly,
      },
      {
        value: ModernizationClassEnum.light,
        label: t("modernizationValue.light"),
        description: t("modernizationValue.light.description"),
        disabled: !!readonly,
      },
      {
        value: ModernizationClassEnum.medium,
        label: t("modernizationValue.medium"),
        description: t("modernizationValue.medium.description"),
        disabled: !!readonly,
      },
      {
        value: ModernizationClassEnum.most,
        label: t("modernizationValue.most"),
        description: t("modernizationValue.most.description"),
        disabled: !!readonly,
      },
      {
        value: ModernizationClassEnum.full,
        label: t("modernizationValue.full"),
        description: t("modernizationValue.full.description"),
        disabled: !!readonly,
      },
    ],
    [t, readonly],
  );

  const selectedModernizationValueOption = modernizationClassOptions.find(
    (option) => option.value === modernization.class,
  );
  const selectedModernizationValueDescription = selectedModernizationValueOption?.description;
  const selectedModernizationValueLabel = selectedModernizationValueOption?.label;

  const costDescriptionMaxLength = 500;
  return (
    <StyledFormGridContainer>
      <StyledFormSection>
        <StyledFormSectionTitle>{t("modernizationsMethod.title")}</StyledFormSectionTitle>
        <FormControlSelect
          name="modernization.method"
          control={control}
          options={modernizationOptions}
          fullWidth
          selectSx={{ height: "45px" }}
          disabled={!!readonly}
          data-cy="refine-data-modernization-method"
        />
      </StyledFormSection>
      {modernization.method === ModernizationMethodEnum.automatic ? (
        <Box
          sx={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: "8px" }}
          data-cy="refine-data-modernization-automatic-description"
        >
          <InfoIcon sx={{ color: "primary.main", fontSize: theme.analysisLayout.refinementInfoIconSize }} />
          <Typography>{t("modernizationsMethod.values.fullyAutomatic.description")}</Typography>
        </Box>
      ) : (
        <Box />
      )}

      {modernization.method !== ModernizationMethodEnum.automatic && (
        <>
          <StyledFormSection sx={{ pt: "30px" }}>
            <StyledFormSectionTitle>{t("modernization.coreRenovationYear")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="modernization.refurbishmentYear"
              control={control}
              fullWidth
              error={!!errors.modernization?.refurbishmentYear}
              helperText={getErrorMessage(errors.modernization?.refurbishmentYear)}
              disabled={!!readonly}
              data-cy="refine-data-modernization-year"
            />
          </StyledFormSection>
          <div />
        </>
      )}
      {/* über Punktrastermethode */}
      {modernization.method === ModernizationMethodEnum.detailed && (
        <>
          <Box sx={{ gridColumn: "span 2", pt: "30px" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.floorCoverings")}
              control={control}
              name="modernization.matrix.interiorDesign"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-interior-design"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.sanitaryArea")}
              control={control}
              name="modernization.matrix.sanitaryArea"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-sanitary-area"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.heatingSystem")}
              control={control}
              name="modernization.matrix.heatingSystem"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-heating-system"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.pipes")}
              control={control}
              name="modernization.matrix.pipes"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-pipes"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.windows")}
              control={control}
              name="modernization.matrix.windows"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-windows"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.thermalInsulation")}
              control={control}
              name="modernization.matrix.thermalInsulation"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-thermal-insulation"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.roofCoveringAndInsulation")}
              control={control}
              name="modernization.matrix.roofCoveringAndInsulation"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-roof-covering"
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("modernization.roomLayout")}
              control={control}
              name="modernization.matrix.roomLayout"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={modernizationMatrixOptions}
              data-cy="refine-data-modernization-matrix-room-layout"
            />
          </Box>
        </>
      )}
      {/* über Modernisierungsklassen */}
      {modernization.method === ModernizationMethodEnum.simple && (
        <Box sx={{ gridColumn: "span 2", pt: "30px" }}>
          <FormControlToggleButtonGroup
            label={t("modernizationClass.title")}
            control={control}
            name="modernization.class"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={modernizationClassOptions}
            data-cy="refine-data-modernization-class"
          />
          {selectedModernizationValueDescription && (
            <Typography sx={{ mt: "22px" }} data-cy="refine-data-modernization-class-description">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {selectedModernizationValueLabel}:
              </Box>{" "}
              {selectedModernizationValueDescription}
            </Typography>
          )}
        </Box>
      )}
      {/* über Modernisierungskosten */}
      {modernization.method === ModernizationMethodEnum.costs && (
        <>
          <StyledFormSection sx={{ pt: "30px" }}>
            <StyledFormSectionTitle>{t("modernizationCost")}</StyledFormSectionTitle>
            <FormControlNumberInput
              name="modernization.cost"
              control={control}
              fullWidth
              error={!!errors.modernization?.cost}
              helperText={getErrorMessage(errors.modernization?.cost)}
              disabled={!!readonly}
              data-cy="refine-data-modernization-cost"
            />
          </StyledFormSection>
          <Box sx={{ gridColumn: "span 2" }}>
            <StyledFormSection>
              <StyledFormSectionTitle>{t("costDescription")}</StyledFormSectionTitle>
              <FormControlTextField
                name="modernization.costDescription"
                control={control}
                multiline
                rows={3}
                disabled={!!readonly}
                inputProps={{ maxLength: costDescriptionMaxLength }}
                helperText={`${modernization?.costDescription?.length || 0}/ ${costDescriptionMaxLength}`}
                FormHelperTextProps={{
                  sx: { textAlign: "right" },
                }}
                data-cy="refine-data-modernization-cost-description"
              />
            </StyledFormSection>
          </Box>
        </>
      )}
    </StyledFormGridContainer>
  );
};
